import { useEffect, useMemo, useRef, useState } from "react";

import { ToDoInfo } from "@/pages/Private/pages/ToDo/ToDoList/schema/to-do";

import { SelfAssessmentStepOption } from "@/types/self-assessment";
import { trackGapStart, trackGapStep, trackGapComplete } from "@/utils/analytics";

import SelfAssessmentSteps from "./SelfAssessmentSteps";

export interface AllQuestionsFromToDosAndSubtasks extends ToDoInfo {
	todoId?: number;
	isSubtask: boolean;
	subtaskId?: string;
	questionDescription: string;
	category?: string;
}

interface SelfAssessmentFlowProps {
	filteredToDos: ToDoInfo[];
	onComplete: () => void;
}

export default function SelfAssessmentFlow({ filteredToDos, onComplete }: SelfAssessmentFlowProps) {
	const [isStarted] = useState(true);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);

	const [isFinished, setIsFinished] = useState(false);

	const [answers, setAnswers] = useState<SelfAssessmentStepOption[]>([]);

	// Navigation history stack to track visited step indices for proper back navigation
	const [_navigationHistory, setNavigationHistory] = useState<number[]>([0]);

	const hasTrackedStart = useRef(false);

	const allQuestionsFromToDosAndSubtasks: AllQuestionsFromToDosAndSubtasks[] = useMemo(() => {
		return filteredToDos
			.sort((a, b) => {
				const orderA = a.order ?? 0;
				const orderB = b.order ?? 0;

				if (orderA > orderB) {
					return 1;
				} else if (orderA < orderB) {
					return -1;
				}

				return 0;
			})
			.flatMap((toDo) => [
				{
					...toDo,
					todoId: toDo.id,
					isSubtask: false,
					subtaskId: undefined,
				},
				...(toDo.subTasks?.map((subTask) => {
					return {
						...toDo,
						...subTask,
						todoId: toDo.id,
						isSubtask: true,
						subtaskId: subTask.id,
						category: toDo.title,
					};
				}) ?? []),
			]) as AllQuestionsFromToDosAndSubtasks[];
	}, [filteredToDos]);

	const subTasksOfCurrentStep = useMemo(() => {
		const AllsubTaskTotal = allQuestionsFromToDosAndSubtasks.filter((sub) => sub.isSubtask);

		const subTasksOfCurrentStep = AllsubTaskTotal.filter(
			(todo) => todo.todoId === allQuestionsFromToDosAndSubtasks[currentStepIndex]?.todoId
		);

		return subTasksOfCurrentStep;
	}, [currentStepIndex, isStarted, allQuestionsFromToDosAndSubtasks]);

	const activeSubStep = useMemo(() => {
		const currentQuestion = allQuestionsFromToDosAndSubtasks[currentStepIndex];

		if (!currentQuestion) {
			return 0;
		}

		return (
			subTasksOfCurrentStep.findIndex((toDo) => toDo.subtaskId === currentQuestion.subtaskId) + 1
		);
	}, [currentStepIndex, subTasksOfCurrentStep, allQuestionsFromToDosAndSubtasks]);

	const activeStep = useMemo(() => {
		return (
			filteredToDos?.findIndex(
				(toDo) => toDo?.id === allQuestionsFromToDosAndSubtasks[currentStepIndex]?.todoId
			) + 1
		);
	}, [filteredToDos, allQuestionsFromToDosAndSubtasks, currentStepIndex]);

	// Track GAP analysis start on mount
	useEffect(() => {
		if (!hasTrackedStart.current && filteredToDos?.length > 0) {
			trackGapStart(filteredToDos.length);
			hasTrackedStart.current = true;
		}
	}, [filteredToDos?.length]);

	// Track step changes
	useEffect(() => {
		if (filteredToDos?.length > 0 && allQuestionsFromToDosAndSubtasks[currentStepIndex]) {
			const currentQuestion = allQuestionsFromToDosAndSubtasks[currentStepIndex];
			const stepName = currentQuestion.title || currentQuestion.category || "";

			trackGapStep(activeStep, activeSubStep, stepName);
		}
	}, [
		currentStepIndex,
		activeStep,
		activeSubStep,
		filteredToDos?.length,
		allQuestionsFromToDosAndSubtasks,
	]);

	const changeStepHandler = async (
		currentAnswer: SelfAssessmentStepOption | null,
		toNext = true
	) => {
		if (toNext) {
			// Moving forward
			const isLastStep = currentStepIndex === allQuestionsFromToDosAndSubtasks.length - 1;
			const currentStepIsNoSubtask = !allQuestionsFromToDosAndSubtasks[currentStepIndex]?.isSubtask;

			if (isLastStep && currentAnswer) {
				// Track GAP analysis completion
				trackGapComplete();
				setIsFinished(true);
				onComplete();

				return;
			}

			let nextStepIndex = currentStepIndex + 1;

			// Determine if we should skip subtasks
			const shouldSkipSubtasks =
				currentStepIsNoSubtask && (currentAnswer?.isSkipped || currentAnswer?.answer === "yes");

			if (shouldSkipSubtasks) {
				// Find the next main task (not a subtask)
				nextStepIndex = allQuestionsFromToDosAndSubtasks.findIndex(
					(step, index) => !step.isSubtask && index > currentStepIndex
				);

				if (nextStepIndex === -1) {
					// Track GAP analysis completion
					trackGapComplete();
					setIsFinished(true);
					onComplete();

					return;
				}
			}

			const nextPage = Math.min(nextStepIndex, allQuestionsFromToDosAndSubtasks.length - 1);

			// Push next step to navigation history before moving forward
			setNavigationHistory((prev) => [...prev, nextPage]);
			setCurrentStepIndex(nextPage);
		} else {
			// Moving backward - use navigation history
			setNavigationHistory((prev) => {
				if (prev.length <= 1) {
					// Already at the beginning, can't go back further
					return prev;
				}

				// Remove the current step from history
				const newHistory = prev.slice(0, -1);
				const previousStepIndex = newHistory[newHistory.length - 1];

				// Update the current step index to the previous step
				setCurrentStepIndex(previousStepIndex);

				// Remove the last answer(s) that correspond to the step we're leaving
				setAnswers((prevAnswers) => {
					if (prevAnswers.length === 0) {
						return prevAnswers;
					}

					// Find how many answers to remove (could be multiple if skip was used)
					const currentStepTodoId = allQuestionsFromToDosAndSubtasks[currentStepIndex]?.todoId;
					const previousStepTodoId = allQuestionsFromToDosAndSubtasks[previousStepIndex]?.todoId;

					// If we're going back to a different todo, remove all answers for the current todo
					if (currentStepTodoId !== previousStepTodoId) {
						return prevAnswers.filter((a) => a.todoId !== currentStepTodoId);
					}

					// Otherwise just remove the last answer
					return prevAnswers.slice(0, -1);
				});

				return newHistory;
			});
		}
	};

	return (
		<div className="w-full h-full flex justify-center max-w-[100%]">
			{isStarted && !isFinished && (
				<SelfAssessmentSteps
					activeStep={activeStep}
					activeSubStep={activeSubStep}
					allSubTasksOfCurrentStep={subTasksOfCurrentStep}
					answers={answers}
					handleBack={(currentAnswer: SelfAssessmentStepOption | null) => {
						changeStepHandler(currentAnswer, false);
					}}
					handleNext={(currentAnswer: SelfAssessmentStepOption) => changeStepHandler(currentAnswer)}
					isLoading={false}
					setAnswers={setAnswers}
					stepInfo={allQuestionsFromToDosAndSubtasks?.[currentStepIndex]}
					subTasksOfCurrentStepLength={subTasksOfCurrentStep?.length ?? 0}
					totalStepLength={filteredToDos?.length ?? 0}
					// changeStepWhenSkipHandler={(currentAnswer: SelfAssessmentStepOption) =>
					// 	changeStepWhenSkipHandler(currentAnswer)
					// }
				/>
			)}
		</div>
	);
}
